import { useState, useEffect, useRef, useMemo } from 'react'

// ── Jua Query Engine API V2 ──────────────────────────────────────────
const JUA_BASE = 'https://query.jua.ai/v1/forecast/'

// Retrieve API key from localStorage (set via Account Settings)
export function getJuaApiKey(): string {
  return localStorage.getItem('jua_api_key') || ''
}
export function setJuaApiKey(key: string) {
  localStorage.setItem('jua_api_key', key)
}
export function hasJuaApiKey(): boolean {
  return !!getJuaApiKey()
}

// ── Model ID mapping (app display name → Jua API identifier) ─────────
export const MODEL_ID_MAP: Record<string, string> = {
  'EPT-2': 'ept2',
  'EPT-2 Early': 'ept2_early',
  'EPT-2 HRRR': 'ept2_hrrr',
  'EPT-2 RR': 'ept2_rr',
  'EPT-2e': 'ept2e',
  'EC AIFS': 'ec_aifs',
  'EC-IFS': 'ecmwf_ifs_single',
  'EC IFS': 'ecmwf_ifs_single',
  GFS: 'gfs_global_single',
  'ICON Global': 'icon_global',
  'ICON-EU': 'icon_eu',
  'Microsoft Aurora': 'microsoft_aurora',
  'EC ENS': 'ec_ens',
  'EC Ens Mean': 'ec_ens_mean',
  'GFS Ens Mean': 'gfs_ens_mean',
}

// ── Variable name mapping (app display name → Jua API variable) ──────
export const VARIABLE_MAP: Record<string, string> = {
  // Temperature
  'Temp 2m': 'air_temperature_at_height_level_2m',
  'Air Temperature 2m': 'air_temperature_at_height_level_2m',
  'Dew Point 2m': 'dew_point_temperature_at_height_level_2m',
  // Wind
  'Wind Speed 10m': 'wind_speed_at_height_level_10m',
  'Wind Direction 10m': 'wind_direction_at_height_level_10m',
  'Wind Dir 10m': 'wind_direction_at_height_level_10m',
  'Wind Speed 100m': 'wind_speed_at_height_level_100m',
  'Wind Direction 100m': 'wind_direction_at_height_level_100m',
  'Wind Dir 100m': 'wind_direction_at_height_level_100m',
  // Precipitation & Radiation
  Precipitation: 'total_precipitation',
  'Precipitation 1h': 'total_precipitation',
  'Solar Radiation': 'surface_solar_radiation_downwards',
  'SSRD 1h': 'surface_solar_radiation_downwards',
  // Atmosphere
  'MSL Pressure': 'mean_sea_level_pressure',
  // Cloud Cover
  'Cloud Cover High': 'high_cloud_cover',
  'Cloud Cover Medium': 'medium_cloud_cover',
  'Cloud Cover Low': 'low_cloud_cover',
}

// ── Location mapping (region name OR region ID → coords) ─────────────
// Supports both full names (e.g. "Germany") and short IDs (e.g. "de")
// from LocationSelector / MarketZoneMap
const COORDS: Record<string, { lat: number; lon: number }> = {
  // --- Full region names ---
  UK: { lat: 51.5, lon: -0.12 },
  'United Kingdom': { lat: 51.5, lon: -0.12 },
  Germany: { lat: 52.52, lon: 13.4 },
  Nordic: { lat: 59.33, lon: 18.07 },
  France: { lat: 48.86, lon: 2.35 },
  Spain: { lat: 40.42, lon: -3.7 },
  'Iberian Peninsula': { lat: 40.42, lon: -3.7 },
  Italy: { lat: 41.9, lon: 12.5 },
  Netherlands: { lat: 52.37, lon: 4.9 },
  Poland: { lat: 52.23, lon: 21.01 },
  Sweden: { lat: 59.33, lon: 18.07 },
  Norway: { lat: 59.91, lon: 10.75 },
  Denmark: { lat: 55.68, lon: 12.57 },
  Finland: { lat: 60.17, lon: 24.94 },
  Austria: { lat: 48.21, lon: 16.37 },
  Switzerland: { lat: 46.95, lon: 7.45 },
  Belgium: { lat: 50.85, lon: 4.35 },
  Portugal: { lat: 38.72, lon: -9.14 },
  'Czech Republic': { lat: 50.08, lon: 14.44 },
  'Central Eastern Europe': { lat: 50.08, lon: 14.44 },
  Balkans: { lat: 44.79, lon: 20.47 },
  Benelux: { lat: 50.85, lon: 4.35 },

  // --- City names ---
  Berlin: { lat: 52.52, lon: 13.4 },
  London: { lat: 51.5, lon: -0.12 },
  Paris: { lat: 48.86, lon: 2.35 },
  Madrid: { lat: 40.42, lon: -3.7 },
  Rome: { lat: 41.9, lon: 12.5 },
  Amsterdam: { lat: 52.37, lon: 4.9 },
  Warsaw: { lat: 52.23, lon: 21.01 },
  Stockholm: { lat: 59.33, lon: 18.07 },
  Oslo: { lat: 59.91, lon: 10.75 },
  Munich: { lat: 48.14, lon: 11.58 },
  Hamburg: { lat: 53.55, lon: 9.99 },

  // --- Offshore ---
  'North Sea': { lat: 56.0, lon: 3.0 },
  'Baltic Sea': { lat: 58.0, lon: 20.0 },
  Mediterranean: { lat: 38.0, lon: 15.0 },

  // --- Country IDs (from LocationSelector / MarketZoneMap) ---
  de: { lat: 52.52, lon: 13.4 },
  uk: { lat: 51.5, lon: -0.12 },
  fr: { lat: 48.86, lon: 2.35 },
  es: { lat: 40.42, lon: -3.7 },
  it: { lat: 41.9, lon: 12.5 },
  nl: { lat: 52.37, lon: 4.9 },
  pl: { lat: 52.23, lon: 21.01 },
  se: { lat: 59.33, lon: 18.07 },
  no: { lat: 59.91, lon: 10.75 },
  dk: { lat: 55.68, lon: 12.57 },
  fi: { lat: 60.17, lon: 24.94 },
  at: { lat: 48.21, lon: 16.37 },
  ch: { lat: 46.95, lon: 7.45 },
  be: { lat: 50.85, lon: 4.35 },
  pt: { lat: 38.72, lon: -9.14 },
  cz: { lat: 50.08, lon: 14.44 },
  sk: { lat: 48.15, lon: 17.11 },
  hu: { lat: 47.5, lon: 19.04 },
  ro: { lat: 44.43, lon: 26.1 },
  bg: { lat: 42.7, lon: 23.32 },
  gr: { lat: 37.98, lon: 23.73 },
  hr: { lat: 45.81, lon: 15.98 },
  rs: { lat: 44.79, lon: 20.47 },
  si: { lat: 46.05, lon: 14.51 },
  ie: { lat: 53.35, lon: -6.26 },
  ee: { lat: 59.44, lon: 24.75 },
  lv: { lat: 56.95, lon: 24.11 },
  lt: { lat: 54.69, lon: 25.28 },

  // --- Market region IDs ---
  nordic: { lat: 59.33, lon: 18.07 },
  iberia: { lat: 40.42, lon: -3.7 },
  cee: { lat: 50.08, lon: 14.44 },
  balkans: { lat: 44.79, lon: 20.47 },
  benelux: { lat: 50.85, lon: 4.35 },

  // --- Market zone IDs (bidding zones) ---
  se1: { lat: 67.0, lon: 20.0 },
  se2: { lat: 62.5, lon: 17.3 },
  se3: { lat: 59.3, lon: 18.1 },
  se4: { lat: 55.6, lon: 13.0 },
  no1: { lat: 59.9, lon: 10.7 },
  no2: { lat: 58.1, lon: 8.0 },
  no3: { lat: 63.4, lon: 10.4 },
  no4: { lat: 69.6, lon: 19.0 },
  no5: { lat: 60.4, lon: 5.3 },
  dk1: { lat: 56.2, lon: 9.0 },
  dk2: { lat: 55.7, lon: 12.6 },
  de_lu: { lat: 50.5, lon: 9.5 },
  de_at_lu: { lat: 49.5, lon: 11.0 },
  fi_zone: { lat: 61.5, lon: 24.0 },
  ee_zone: { lat: 58.6, lon: 25.0 },
  lv_zone: { lat: 56.9, lon: 24.1 },
  lt_zone: { lat: 55.2, lon: 24.0 },
  nl_zone: { lat: 52.1, lon: 5.3 },
  be_zone: { lat: 50.5, lon: 4.4 },
  fr_zone: { lat: 46.6, lon: 2.2 },
  es_zone: { lat: 40.0, lon: -3.7 },
  pt_zone: { lat: 39.4, lon: -8.2 },
  at_zone: { lat: 47.5, lon: 14.5 },
  ch_zone: { lat: 46.8, lon: 8.2 },
  pl_zone: { lat: 52.0, lon: 19.4 },
  cz_zone: { lat: 49.8, lon: 15.5 },
  sk_zone: { lat: 48.7, lon: 19.7 },
  hu_zone: { lat: 47.2, lon: 19.5 },
  ro_zone: { lat: 45.9, lon: 25.0 },
  bg_zone: { lat: 42.7, lon: 25.5 },
  gr_zone: { lat: 39.1, lon: 21.8 },
  hr_zone: { lat: 45.1, lon: 15.2 },
  rs_zone: { lat: 44.0, lon: 21.0 },
  si_zone: { lat: 46.1, lon: 14.8 },
  ie_sem: { lat: 53.4, lon: -8.2 },
  gb_zone: { lat: 54.0, lon: -2.0 },
  it_north: { lat: 45.5, lon: 11.0 },
  it_cnorth: { lat: 43.8, lon: 11.2 },
  it_csouth: { lat: 41.9, lon: 13.5 },
  it_south: { lat: 40.5, lon: 16.0 },
  it_sicily: { lat: 37.5, lon: 14.0 },
  it_sardinia: { lat: 40.0, lon: 9.0 },

  // --- City IDs ---
  berlin: { lat: 52.52, lon: 13.4 },
  london: { lat: 51.5, lon: -0.12 },
  paris: { lat: 48.86, lon: 2.35 },
  madrid: { lat: 40.42, lon: -3.7 },
  rome: { lat: 41.9, lon: 12.5 },
  amsterdam: { lat: 52.37, lon: 4.9 },
  warsaw: { lat: 52.23, lon: 21.01 },
  stockholm: { lat: 59.33, lon: 18.07 },
  oslo: { lat: 59.91, lon: 10.75 },
  munich: { lat: 48.14, lon: 11.58 },
  hamburg: { lat: 53.55, lon: 9.99 },

  // --- Offshore IDs ---
  north_sea: { lat: 56.0, lon: 3.0 },
  baltic_sea: { lat: 58.0, lon: 20.0 },
  mediterranean: { lat: 38.0, lon: 15.0 },
}

// ── Types ────────────────────────────────────────────────────────────
export interface JuaForecastRow {
  model: string
  init_time: string
  prediction_timedelta: number // hours
  latitude: number
  longitude: number
  [variable: string]: any
}

export interface JuaForecastResult {
  /** Raw rows from the API, keyed by model */
  byModel: Record<string, { hour: number; value: number }[]>
  /** Whether real data was loaded */
  hasRealData: boolean
  loading: boolean
  error: string | null
}

// ── Cache ────────────────────────────────────────────────────────────
const cache: Record<string, { data: any; timestamp: number }> = {}
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// ── Hook ─────────────────────────────────────────────────────────────
export function useJuaForecast(
  modelNames: string[],
  region: string,
  variable: string,
  maxHours: number,
): JuaForecastResult {
  const [byModel, setByModel] = useState<
    Record<string, { hour: number; value: number }[]>
  >({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRealData, setHasRealData] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  // Map app model names to Jua API IDs
  const juaModels = useMemo(
    () => modelNames.map((n) => MODEL_ID_MAP[n]).filter(Boolean),
    [modelNames],
  )

  // Map app variable name to Jua API variable
  const juaVariable = VARIABLE_MAP[variable]

  // Get coordinates
  const coords = COORDS[region]

  useEffect(() => {
    const apiKey = getJuaApiKey()

    // Skip if no API key, no valid variable mapping, or no coords
    if (!apiKey || !juaVariable || !coords || juaModels.length === 0) {
      setHasRealData(false)
      setLoading(false)
      return
    }

    // Build cache key
    const cacheKey = `jua_${juaModels.sort().join(',')}_${coords.lat}_${coords.lon}_${juaVariable}_${maxHours}`
    const cached = cache[cacheKey]
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setByModel(cached.data)
      setHasRealData(true)
      setLoading(false)
      setError(null)
      return
    }

    // Abort previous
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    setError(null)

    // Build URL with query params
    const params = new URLSearchParams()
    params.set('format', 'json')
    params.set('init_time', 'latest')
    params.set('latitude', String(coords.lat))
    params.set('longitude', String(coords.lon))
    params.set('max_prediction_timedelta', String(maxHours))
    params.set('request_credit_limit', '10')
    // Add each model
    juaModels.forEach((m) => params.append('models', m))
    // Add variable
    params.append('variables', juaVariable)

    const url = `${JUA_BASE}?${params.toString()}`

    fetch(url, {
      headers: {
        'X-API-Key': apiKey,
        Accept: 'application/json',
      },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            throw new Error(
              'Invalid Jua API key. Check your key in Account Settings.',
            )
          }
          if (res.status === 520) {
            throw new Error(
              'Jua API server error (520). The service may be temporarily unavailable. Showing fallback data.',
            )
          }
          throw new Error(`Jua API error: ${res.status}`)
        }
        return res.json()
      })
      .then((json: any[]) => {
        // Parse response — expected format: array of flat objects
        // Each row has: model, init_time, prediction_timedelta, latitude, longitude, <variable_name>
        const result: Record<string, { hour: number; value: number }[]> = {}

        if (Array.isArray(json)) {
          for (const row of json) {
            const modelId = row.model || row.models
            // Find the display name for this model
            const displayName =
              Object.entries(MODEL_ID_MAP).find(
                ([, id]) => id === modelId,
              )?.[0] || modelId

            if (!result[displayName]) result[displayName] = []

            const hour =
              typeof row.prediction_timedelta === 'number'
                ? row.prediction_timedelta
                : parseFloat(row.prediction_timedelta) || 0

            // The value could be under the variable name key or a generic "value" key
            const value = row[juaVariable] ?? row.value ?? row[variable] ?? null

            if (
              value !== null &&
              value !== undefined &&
              !isNaN(Number(value))
            ) {
              result[displayName].push({ hour, value: Number(value) })
            }
          }

          // Sort each model's data by hour
          for (const key of Object.keys(result)) {
            result[key].sort((a, b) => a.hour - b.hour)
          }
        }

        cache[cacheKey] = { data: result, timestamp: Date.now() }
        setByModel(result)
        setHasRealData(Object.keys(result).length > 0)
        setLoading(false)
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        console.warn('Jua forecast error:', err.message || err)
        // Don't set error state for 520 errors - just fall back to synthetic data silently
        if (err.message && err.message.includes('520')) {
          console.warn('Jua API temporarily unavailable. Using synthetic data.')
          setError(null)
        } else {
          setError(err.message)
        }
        setHasRealData(false)
        setLoading(false)
      })

    return () => controller.abort()
  }, [juaModels.join(','), juaVariable, coords?.lat, coords?.lon, maxHours])

  return { byModel, hasRealData, loading, error }
}
